import { useMemo, useSyncExternalStore } from 'react';
import type { LayoutElement } from '../types';
import {
  getElement3DExtrusionDepth,
  getElement3DGroupY,
  resolveRailElevation,
} from '../utils/layout3d';
import { getOhtPivot, getOhtPivotWorld } from '../utils/ohtPivot';
import { replaySnapshotStore } from '../store/replaySnapshotStore';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { liveMotionStore } from '../store/liveMotionStore';
import { useAppSelector } from './redux';
import { selectExportableEquipmentInstanceMap } from '../features/equipment/equipmentSelectors';
import {
  buildLiveStockerIndex,
  liveStockerColumnSplit,
  lookupLiveStocker,
} from '../features/simulation/liveStockerInventory';
import { useLiveInventoryDisplay } from './useLiveInventoryDisplay';
import { resolveOhtCarriedFoupVisibleFromRuntime } from '../features/simulation/ohtCarriedFoupVisible';

export interface FrameVehicle {
  id: string;
  ohtId: string;
  name: string;
  x: number;
  y: number;
  angle: number;
  idleAngle: number;
  w: number;
  h: number;
  color: string;
  pivotX: number;
  pivotY: number;
  graphicUrl?: string;
  groupY: number;
  meshDepth: number;
  isPaused?: boolean;
  stopElapsedS?: number;
  stopDurationS?: number | null;
  stopLoadedAction?: boolean;
  /** Load-port id from the backend PICKUP/DROP event for this dwell. */
  transferPortId?: string;
  isLoaded?: boolean;
  /** Travel / Deadhead / Wait / Idle — same labels as the HTML player. */
  phaseLabel?: string;
  phaseChip?: 'travel' | 'deadhead' | 'wait' | 'idle';
}

export type SimulationInventoryCounts = {
  /** Live FOUP counts keyed by layout stocker id (from PICKUP/DROP events). */
  stockerFoups: Record<string, number>;
  /** Live FOUP counts keyed by equipment/tool instance id (LOAD+UNLOAD). */
  toolFoups: Record<string, number>;
};

export type SimulationFramesResult = SimulationInventoryCounts & {
  vehicles: FrameVehicle[];
};

function getBackendOhtId(element: LayoutElement): string | null {
  const configuredId = element.customProperties?.ohtId;
  return typeof configuredId === 'string' && configuredId.length > 0 ? configuredId : null;
}

/**
 * OHT elements keyed by every id the backend might name them by, so resolving
 * a streamed vehicle is a lookup rather than a scan of the OHT layer per
 * vehicle (which made shell rebuilds quadratic in fleet size).
 */
function indexOhtElements(ohtElements: LayoutElement[]): Map<string, LayoutElement> {
  const index = new Map<string, LayoutElement>();
  for (const element of ohtElements) {
    const amhsId = element.customProperties?.amhsId;
    const keys = [
      element.id,
      getBackendOhtId(element),
      element.metadata.name,
      typeof amhsId === 'string' ? amhsId : null,
    ];
    for (const key of keys) {
      // First element wins, matching the previous `find` over layer order.
      if (key && !index.has(key)) index.set(key, element);
    }
  }
  return index;
}

/**
 * Vehicle shells only.
 *
 * Split from the FOUP counts so an overlay that paints OHTs does not also
 * rebuild inventory maps (and vice versa) on every UI clock sync.
 * Shells are stable across the 10Hz UI clock; pose is applied imperatively
 * from simulationClockStore.
 */
export function useSimulationVehicles(): FrameVehicle[] {
  const elements = useAppSelector((state) => state.layout.elements);
  const running = useAppSelector((state) => state.simulation.running);
  const playbackVehicles = useAppSelector((state) => state.simulation.playbackVehicles);
  const browserKinetic = useAppSelector((state) => state.simulation.browserKinetic);
  const replayPlan = useAppSelector((state) => state.simulation.replayPlan);
  const source = useAppSelector((state) => state.simulation.source);
  const scaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  // Live shells only rebuild when the fleet set changes — not on every leg flush.
  const liveFleetSignature = useSyncExternalStore(
    (onStoreChange) => liveMotionStore.subscribe(onStoreChange),
    () => (source === 'live' ? liveMotionStore.getFleetSignature() : ''),
    () => ''
  );

  // Keyed on the layout alone. The stream hands `shells` a new browserKinetic,
  // playbackVehicles and ohtTimelines on every flush, so anything derived from
  // the whole element map has to sit outside that memo or a complex layout gets
  // re-walked ten times a second.
  const layout = useMemo(() => {
    const layoutList = Object.values(elements);
    const ohtElements = layoutList.filter(
      (element) => element.type === 'oht-vehicle' && element.metadata.visible
    );
    return {
      ohtElements,
      ohtIndex: indexOhtElements(ohtElements),
      // Vehicles hang off the rail, so they follow the same lifted plane.
      railElevation: resolveRailElevation(layoutList),
    };
  }, [elements]);

  const shells = useMemo(() => {
    if (!running) return [] as FrameVehicle[];

    const { ohtElements, ohtIndex, railElevation } = layout;
    const renderedLayoutIds = new Set<string>();
    const renderedOhtIds = new Set<string>();

    const liveMotion = source === 'live' ? liveMotionStore.getSnapshot() : null;
    const activePlayback = liveMotion?.playbackVehicles ?? playbackVehicles;
    const activeIdle =
      liveMotion?.idleByVehicle ?? browserKinetic?.vehicleLegs.idleByVehicle;
    const activeTimelines = liveMotion?.ohtTimelines ?? {};
    const hasMotionSource = liveMotion
      ? liveMotion.fleetSignature.length > 0
      : Boolean(browserKinetic);

    const streamed = hasMotionSource
      ? Object.values(activePlayback).flatMap((playback) => {
        renderedOhtIds.add(playback.ohtId);
        const idle = activeIdle?.[playback.ohtId];
        if (!idle) return [];

        const layoutOht = ohtIndex.get(playback.ohtId);
        if (layoutOht) renderedLayoutIds.add(layoutOht.id);
        const idleAngle = layoutOht?.rotation ?? 0;
        const w = layoutOht?.dimensions.width ?? 12;
        const h = layoutOht?.dimensions.height ?? 7;
        // Centre of the vehicle (not 0,0) — 0,0 hung the body off the rail edge.
        const { pivotX, pivotY } = layoutOht
          ? getOhtPivot(layoutOht, w, h)
          : { pivotX: w / 2, pivotY: h / 2 };

        const layoutSeed = layoutOht ? getOhtPivotWorld(layoutOht, w, h) : idle;
        const layoutSeedX = layoutSeed.x;
        const layoutSeedY = layoutSeed.y;
        const layoutSeedAngle = layoutOht?.rotation ?? idleAngle;

        return [
          {
            id: layoutOht?.id ?? playback.ohtId,
            ohtId: playback.ohtId,
            name: layoutOht?.metadata.name || playback.ohtId,
            // Keep bay-level OHTs at their layout pose until the live scheduler
            // reports the first MOVING leg (SimulationOverlay RAF defers runtime).
            x: layoutSeedX,
            y: layoutSeedY,
            angle: layoutSeedAngle,
            idleAngle: layoutSeedAngle,
            w,
            h,
            color: layoutOht?.metadata.color || '#f59e0b',
            pivotX,
            pivotY,
            graphicUrl: '/oht_withoutload.png',
            groupY: layoutOht ? getElement3DGroupY(layoutOht, scaleFactor, railElevation) : 0,
            meshDepth: layoutOht ? getElement3DExtrusionDepth(layoutOht) : 40,
            isLoaded: false,
          } satisfies FrameVehicle,
        ];
      })
      : [];

    // Starting a live stream hides the editable OHT layer immediately. Keep
    // layout vehicles in the simulation layer until the backend publishes a
    // leg for each one; otherwise idle/unassigned OHTs disappear entirely.
    const layoutFallbacks = ohtElements
      .filter((element) => !renderedLayoutIds.has(element.id))
      .map((element) => {
        const w = element.dimensions.width;
        const h = element.dimensions.height;
        const { pivotX, pivotY } = getOhtPivot(element, w, h);
        const pivotWorld = getOhtPivotWorld(element, w, h);
        return {
          id: element.id,
          ohtId: getBackendOhtId(element) ?? element.id,
          name: element.metadata.name || element.id,
          // The simulation group uses its pivot as the position, while layout
          // elements store their top-left corner and rotate about it.
          x: pivotWorld.x,
          y: pivotWorld.y,
          angle: element.rotation,
          idleAngle: element.rotation,
          w,
          h,
          color: element.metadata.color || '#f59e0b',
          pivotX,
          pivotY,
          graphicUrl: '/oht_withoutload.png',
          groupY: getElement3DGroupY(element, scaleFactor, railElevation),
          meshDepth: getElement3DExtrusionDepth(element),
          isLoaded: false,
        } satisfies FrameVehicle;
      });

    const streamOnlyFallbacks = Object.entries(activeTimelines)
      .filter(([ohtId]) => !renderedOhtIds.has(ohtId))
      .flatMap(([ohtId, events]) => {
        if (ohtIndex.has(ohtId)) return [];
        const first = events[0];
        const point =
          first?.type === 'vehicle_leg'
            ? { x: first.leg.fromX, y: first.leg.fromY }
            : first?.type === 'junction_wait'
              ? { x: first.frame.x ?? 0, y: first.frame.y ?? 0 }
              : first?.type === 'transfer_event'
                ? { x: first.frame.x ?? 0, y: first.frame.y ?? 0 }
                : { x: 0, y: 0 };
        return [
          {
            id: ohtId,
            ohtId,
            name: ohtId,
            x: point.x,
            y: point.y,
            angle: 0,
            idleAngle: 0,
            w: 12,
            h: 7,
            color: '#f59e0b',
            pivotX: 6,
            pivotY: 3.5,
            graphicUrl: '/oht_withoutload.png',
            groupY: railElevation,
            meshDepth: 40,
            isLoaded: false,
          } satisfies FrameVehicle,
        ];
      });

    return [...streamed, ...layoutFallbacks, ...streamOnlyFallbacks];
  }, [
    browserKinetic,
    layout,
    liveFleetSignature,
    playbackVehicles,
    running,
    scaleFactor,
    source,
  ]);

  const liveCarrySignature = useSyncExternalStore(
    (notify) => liveOhtRuntimeStore.subscribe(() => notify()),
    () => {
      if (source !== 'live') return '';
      return shells
        .map((v) =>
          resolveOhtCarriedFoupVisibleFromRuntime(liveOhtRuntimeStore.getOht(v.ohtId))
            ? '1'
            : '0'
        )
        .join('');
    },
    () => ''
  );

  const loadedSignature = useMemo(() => {
    if (source === 'live') return liveCarrySignature;
    if (!replayPlan) return '';
    const snapshot = replaySnapshotStore.getSnapshot();
    return shells.map((v) => (snapshot?.ohts[v.ohtId]?.loadedFoupId ? '1' : '0')).join('');
  }, [liveCarrySignature, replayPlan, shells, source]);

  const vehicles = useMemo(() => {
    if (!browserKinetic) return shells;
    return shells.map((v, index) => {
      const isLoaded = loadedSignature[index] === '1';
      return {
        ...v,
        isLoaded,
        graphicUrl: isLoaded ? '/oht_withload.png' : '/oht_withoutload.png',
        // Phase chip is updated imperatively from the RAF clock — keep shells
        // stable so 10Hz scrubber sync does not remount Konva OHTs.
        phaseLabel: 'Idle',
        phaseChip: 'idle' as const,
      };
    });
  }, [browserKinetic, loadedSignature, shells]);

  return vehicles;
}

/** Live / replay FOUP counts, independent of vehicle shells. */
export function useSimulationInventoryCounts(): SimulationInventoryCounts {
  const elements = useAppSelector((state) => state.layout.elements);
  const equipmentInstances = useAppSelector(selectExportableEquipmentInstanceMap);
  const running = useAppSelector((state) => state.simulation.running);
  const simulationTime = useAppSelector((state) => state.simulation.simulationTime);
  const browserKinetic = useAppSelector((state) => state.simulation.browserKinetic);
  const replayPlan = useAppSelector((state) => state.simulation.replayPlan);
  const source = useAppSelector((state) => state.simulation.source);
  const liveInventory = useAppSelector((state) => state.simulation.live.inventory);
  const displayInventory = useLiveInventoryDisplay();
  const inventoryForCounts =
    source === 'live' && running ? (displayInventory ?? liveInventory) : liveInventory;

  const stockerFoupSignature = useMemo(() => {
    if (!running) return '';
    const live = (inventoryForCounts?.stockers ?? [])
      .map(
        (stocker) =>
          `${stocker.stockerId}:${stocker.occupancy ?? ''}:${stocker.rawOccupancy ?? stocker.newOccupancy ?? ''}:${stocker.processedOccupancy ?? ''}`
      )
      .sort()
      .join('|');
    const snapshot = replaySnapshotStore.getSnapshot();
    const counts = snapshot?.stockers ?? {};
    const replay = Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, stocker]) => `${id}:${stocker.inventory}`)
      .join('|');
    return `${live}#${replay}`;
  }, [inventoryForCounts, replayPlan, running, simulationTime]);

  // Only the stockers, resolved once per layout edit. Walking the whole element
  // map here cost a full pass over a complex layout on every inventory frame.
  const stockerElements = useMemo(
    () => Object.values(elements).filter((element) => element.type === 'stocker'),
    [elements]
  );

  const liveStockerIndex = useMemo(
    () => buildLiveStockerIndex(inventoryForCounts),
    [inventoryForCounts]
  );

  const stockerFoups = useMemo(() => {
    if (!stockerFoupSignature) return {};
    const snapshot = replaySnapshotStore.getSnapshot();
    const counts: Record<string, number> = {};
    for (const element of stockerElements) {
      const liveStocker = lookupLiveStocker(liveStockerIndex, element.id, [
        element.metadata.name,
        element.customProperties?.stockerId,
        element.customProperties?.amhsId,
      ]);
      const liveColumns = liveStockerColumnSplit(liveStocker);
      if (liveStocker) {
        counts[element.id] =
          typeof liveStocker.occupancy === 'number'
            ? liveStocker.occupancy
            : (liveColumns?.raw ?? 0) + (liveColumns?.processed ?? 0);
        continue;
      }
      const ownerIds = new Set<string>([element.id]);
      const ports = element.customProperties?.ports;
      if (Array.isArray(ports)) {
        for (const port of ports as Array<{ id?: string }>) {
          if (!port.id) continue;
          const ownerId = browserKinetic?.stockerPortOwners?.[port.id];
          if (ownerId) ownerIds.add(ownerId);
        }
      }
      counts[element.id] =
        [...ownerIds]
          .map((id) => snapshot?.stockers[id]?.inventory)
          .find((value): value is number => value !== undefined) ?? 0;
    }
    for (const instance of Object.values(equipmentInstances)) {
      if (instance.objectType !== 'stocker') continue;
      const liveStocker = lookupLiveStocker(liveStockerIndex, instance.id, [
        instance.name,
        instance.equipmentType,
      ]);
      if (!liveStocker) continue;
      const liveColumns = liveStockerColumnSplit(liveStocker);
      counts[instance.id] =
        typeof liveStocker.occupancy === 'number'
          ? liveStocker.occupancy
          : (liveColumns?.raw ?? 0) + (liveColumns?.processed ?? 0);
    }
    return counts;
  }, [
    stockerFoupSignature,
    stockerElements,
    equipmentInstances,
    browserKinetic,
    liveStockerIndex,
  ]);

  const toolFoupSignature = useMemo(() => {
    if (!running || !replayPlan) return '';
    const counts = replaySnapshotStore.getSnapshot()?.equipment ?? {};
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([id, tool]) => `${id}:${tool.inventory}:${tool.status ?? ''}`)
      .join('|');
  }, [replayPlan, running, simulationTime]);

  const toolFoups = useMemo(() => {
    if (!toolFoupSignature || !replayPlan) return {};
    const snapshot = replaySnapshotStore.getSnapshot();
    const counts: Record<string, number> = {};
    for (const instance of Object.values(equipmentInstances)) {
      const ownerIds = new Set<string>([instance.id, instance.name]);
      for (const port of instance.overrides?.ports ?? []) {
        const ownerId = browserKinetic?.toolPortOwners?.[port.id];
        if (ownerId) ownerIds.add(ownerId);
      }
      counts[instance.id] =
        [...ownerIds]
          .map((id) => snapshot?.equipment[id]?.inventory)
          .find((value): value is number => value !== undefined) ?? 0;
    }
    return counts;
  }, [toolFoupSignature, replayPlan, equipmentInstances, browserKinetic]);

  return useMemo(() => ({ stockerFoups, toolFoups }), [stockerFoups, toolFoups]);
}

/** Vehicles plus FOUP counts — for consumers that genuinely need both. */
export function useSimulationFrames(): SimulationFramesResult {
  const vehicles = useSimulationVehicles();
  const { stockerFoups, toolFoups } = useSimulationInventoryCounts();
  return useMemo(
    () => ({ vehicles, stockerFoups, toolFoups }),
    [vehicles, stockerFoups, toolFoups]
  );
}
